/**
 * Super-admin "Users" domain (OTHER users an agency manages - NOT the logged
 * -in session's own user record, which context/user.js already owns) - built
 * on apiClient via useRequest, not useAppServices(). Shared by:
 *   - Users/components/data-tables/data/dataTableData.js (paginated list)
 *   - Users/components/CreateModal/index.js (create)
 *   - Users/components/EditModal/index.js (update)
 *   - Users/components/DelModal/index.js (delete)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const userKeys = {
  all: ["users"],
  list: (agencyId, page, search) => ["users", agencyId, page, search],
};

export function useUsersList({ agencyId, page, search, limit = 10 }, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: userKeys.list(agencyId, page, search),
    queryFn: async () => {
      const { response } = await request(GET, "/users", {
        query: `agency_id=${agencyId}&page=${page}&limit=${limit}&search=${search === undefined ? "" : search}`,
        toaster: false,
      });
      // Backend shape is { data: { records, page, limit, totalPages }, accounts }
      // - flattened here so dataTableData.js can read List.records/.page/etc directly.
      return {
        records: response?.data?.records || [],
        page: response?.data?.page,
        limit: response?.data?.limit,
        totalPages: response?.data?.totalPages || 1,
        accounts: response?.accounts || [],
      };
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreateUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/users", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/users", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useDeleteUser() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/users", { query }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}
